import { Editor, NodeEntry, Path, Element as SlateElement, Transforms } from 'slate'

import {
  BLOCK_FORMATS,
  CustomEditor,
} from 'core-components/library/richTextEditor/interface/custom-types'
import { TCell } from 'core-components/library/richTextEditor/interface/TCell'

import { createCell, createRow, getGridTable, isTable } from './commonTableHelper'

type TSplitableNode = {
  splitableNode: NodeEntry | undefined
  canSplitToRow: boolean
}

export const canSplit = (editor: CustomEditor) => {
  const result: TSplitableNode = { splitableNode: undefined, canSplitToRow: true }
  if (!editor.selection || !isTable(editor)) {
    return result
  }

  const [splitable] = Editor.edges(editor, editor.selection)
  const [splitableNode] = Editor.nodes(editor, {
    match: (node) => SlateElement.isElement(node) && node.type === BLOCK_FORMATS.tableCell,
    at: splitable,
  })
  result.splitableNode = splitableNode as NodeEntry

  result.canSplitToRow = (splitableNode?.[0] as TCell)?.rowspan === 1

  return result
}

export const splitCellToRow = (editor: CustomEditor) => {
  const { splitableNode, canSplitToRow } = canSplit(editor)

  if (!splitableNode || !canSplitToRow) {
    return
  }

  const { gridTable, yIndex } = getGridTable(editor)

  const newRow = createRow({
    columns: 1,
    rowspan: (splitableNode[0] as TCell).rowspan,
    colspan: (splitableNode[0] as TCell).colspan,
  })

  Transforms.insertNodes(editor, newRow, {
    at: Path.next(Path.parent(splitableNode[1])),
  })

  gridTable[splitableNode[1][yIndex]].forEach((cell) => {
    if (!Path.equals(cell.originPath, splitableNode[1])) {
      Transforms.setNodes(
        editor,
        { rowspan: cell.cell.rowspan + 1 },
        {
          at: cell.originPath,
        }
      )
    }
  })
}

export const splitCellToColumn = (editor: CustomEditor) => {
  if (!editor.selection || !isTable(editor)) {
    return
  }
  const { gridTable, xIndex } = getGridTable(editor)

  const [splitable] = Editor.edges(editor, editor.selection)
  const [splitableNode] = Editor.nodes(editor, {
    match: (node) => SlateElement.isElement(node) && node.type === BLOCK_FORMATS.tableCell,
    at: splitable,
  })

  if (!splitableNode) {
    return
  }

  const newCell = createCell(
    (splitableNode[0] as TCell).colspan === 1
      ? {
          rowspan: (splitableNode[0] as TCell).rowspan,
          colspan: (splitableNode[0] as TCell).colspan,
        }
      : { rowspan: (splitableNode[0] as TCell).rowspan }
  )
  Transforms.insertNodes(editor, newCell, {
    at: Path.next(splitableNode[1]),
  })

  const colPath = splitableNode[1][xIndex]

  gridTable.forEach((row) => {
    if (!Path.equals(row[colPath].originPath, splitableNode[1])) {
      Transforms.setNodes(
        editor,
        { colspan: row[colPath].cell.colspan + 1 },
        {
          at: row[colPath].originPath,
        }
      )
    }
  })
}
